import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { cloneDeep } from 'lodash';

import { I18nService, untilDestroyed, UrlParamsService } from '@app/core';
import { DocumentInput, Folder, FooterEnum, FooterTypeEnum, ReminderEnum, SignatureAppearanceEnum, User, EmailTemplate } from '@app/models';
import { AppService, DateService, UserService } from '@app/services';
import { FoldersListingService } from '@app/pages/documents/shared/folders-listing.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectOption } from '@app/shared';

@Component({
  selector: 'app-document-configs-form',
  templateUrl: './document-configs-form.component.html',
  styleUrls: ['./document-configs-form.component.scss'],
  standalone: false
})
export class DocumentConfigsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() document: DocumentInput;
  @Input() documentCreatedAt: string;
  @Input() validationErrors: { [k: string]: any };
  @Input() isNewDocument = false;
  @Input() isQualified = false;
  @Input() isPdfA = false;
  @Output() documentChange = new EventEmitter<DocumentInput>();
  currentUser: User;
  isLockedCC = false;
  isLockedReplyTo = false;
  isLockedWatchers = false;
  showReminder = false;
  showExpiration = false;
  showDeadline = false;
  showNewSignatureStyle = false;
  showAuditPage = true;
  isUpdatedUserData = true;
  deadline: { date?: string; time?: string } = {};
  cc = '';
  replyTo = '';
  folder: { id: string; name: string };
  isMobile: boolean;
  defaultEmailTemplate: EmailTemplate;

  readonly ReminderEnum = ReminderEnum;
  readonly FooterEnum = FooterEnum;
  readonly FooterTypeEnum = FooterTypeEnum;
  readonly SignatureAppearanceEnum = SignatureAppearanceEnum;
  readonly timezones: SelectOption[] = [];
  readonly dateFormat: SelectOption[] = [];

  constructor(
    public appService: AppService,
    public translateService: TranslateService,
    public urlParamsService: UrlParamsService,
    private userService: UserService,
    private foldersListingService: FoldersListingService,
    private i18nService: I18nService,
    private dateService: DateService
  ) {
    this.timezones = this.i18nService.timezonesWithGMT || null;
    this.dateFormat = this.i18nService.dateFormatOptions || null;
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    this.userService
      .watchCurrentUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: user => (this.currentUser = user)
      });

    this.userService.getCurrentUser().subscribe({
      next: user => {
        if (user) {
          this.document.message = this.document.message || user.settings.email.text;
          if (this.isNewDocument) {
            this.document.configs = { notification_signed: true, notification_finished: false, notify_watchers: false, lock_user_data: false, signature_appearance: null, ...this.document.configs };
          }
          if (user.subscription.has_premium_features && this.isNewDocument) {
            this.switchChangeExpiration();
          }
          this.toggleSwitches(user);

          if (user.additional_configs.show_audit_page === false && this.isNewDocument) {
            this.showAuditPage = false;
            this.document.show_audit_page = false;
          }
          if (!this.isNewDocument) {
            this.showAuditPage = this.document.show_audit_page;
          }
          if (!this.document.qualified) {
            this.isUpdatedUserData = !this.document.configs?.lock_user_data;
          }
          if (user.member.configs.folder) {
            this.folder = user.member.configs.folder;
          }
          if (user.subscription.has_premium_features && this.isNewDocument && this.document?.cc?.length > 0 && !this.cc) {
            this.setDocumentCC(this.document);
          }
          if (user.subscription.has_premium_features && this.isNewDocument && user.member.configs.locked_watchers?.length > 0) {
            this.document.watchers = user.member.configs.locked_watchers;
          }
          if (user.additional_configs.notify_watchers !== undefined && this.isNewDocument && this.document.configs) {
            this.document.configs.notify_watchers = user.additional_configs.notify_watchers;
          }
        }
      }
    });
  }

  ngOnDestroy() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isQualified) {
      this.selectNewSignatureStyle(this.document.new_signature_style);
    }
    if ((changes.document || {}).currentValue) {
      if (changes.document.currentValue.cc !== (changes.document.previousValue || {}).cc) {
        const documentInput = changes.document.currentValue;
        this.setDocumentCC(documentInput);
      }
      if (changes.document.currentValue.reply_to !== (changes.document.previousValue || {}).reply_to) {
        this.replyTo = changes.document.currentValue.reply_to;
      }
      if (changes.document.currentValue !== changes.document.previousValue) {
        if ((changes.document.currentValue.expiration || {}).notify_at) {
          changes.document.currentValue.expiration.notify_at = this.dateService.normalizeDate(new Date(changes.document.currentValue.expiration.notify_at).toLocaleDateString('pt-BR'));
        }
        if (changes.document.currentValue.deadline_at) {
          this.showDeadline = true;
          const deadlineAt = new Date(changes.document.currentValue.deadline_at);
          this.deadline.date = this.dateService.normalizeDate(deadlineAt.toLocaleDateString('pt-BR'));
          this.deadline.time = deadlineAt.toLocaleTimeString('pt-BR').substring(0, 5);
        } else {
          this.showDeadline = false;
        }
        this.toggleSwitches();
      }
    }
  }

  setDocumentCC(document: DocumentInput) {
    this.cc = ((document.cc || []) as DocumentInput['cc']).map(cc => cc.email).join(', ');
  }

  parsedDocumentInput(document?: DocumentInput): DocumentInput {
    const documentClone = cloneDeep(document || this.document);
    documentClone.name = (documentClone.name || '-').trim(); // Back-end dá um erro zoado, vazio não adianta. Se o valor tiver menos de 3 caracteres dá um erro interpretável.
    if (documentClone.qualified) {
      this.deleteDocumentAttribute(documentClone.configs || {}, 'lock_user_data', false);
    }
    if (!documentClone.footer) {
      this.deleteDocumentAttribute(documentClone, 'footer', null);
    }
    if (!documentClone.refusable) {
      this.deleteDocumentAttribute(documentClone, 'stop_on_rejected', true);
    }
    if (!this.showReminder || !this.currentUser.subscription.has_premium_features) {
      this.deleteDocumentAttribute(documentClone, 'reminder', this.currentUser.subscription.has_premium_features ? null : undefined);
    }
    if (!this.showDeadline || !(this.deadline.date && this.deadline.time && this.deadline.date.match(/[0-9]{2}\/[0-9]{2}\/[0-9]{4}/) && this.deadline.time.match(/[0-9]{2}:[0-9]{2}/))) {
      this.deleteDocumentAttribute(documentClone, 'deadline_at', null);
    } else {
      const deadlineAt = new Date(this.dateService.normalizeDate(this.deadline.date).split('/').reverse().join('-') + ' ' + this.deadline.time).toJSON();
      if (deadlineAt) {
        documentClone.deadline_at = deadlineAt;
      } else {
        this.deleteDocumentAttribute(documentClone, 'deadline_at', null);
      }
    }
    if (
      !this.showExpiration ||
      !this.currentUser.subscription.has_premium_features ||
      !((documentClone.expiration || {}).notify_at || '').match(/[0-9]{2}\/[0-9]{2}\/[0-9]{4}/) ||
      !parseInt((documentClone.expiration || {}).days_before as any)
    ) {
      this.deleteDocumentAttribute(documentClone, 'expiration', this.currentUser.subscription.has_premium_features ? null : undefined);
    } else {
      documentClone.expiration.notify_at = this.dateService.normalizeDate(documentClone.expiration.notify_at);
    }
    if (!this.currentUser.subscription.has_premium_features) {
      this.deleteDocumentAttribute(documentClone, 'cc');
      this.deleteDocumentAttribute(documentClone, 'reply_to');
      this.deleteDocumentAttribute(documentClone, 'configs');
    } else {
      documentClone.cc = (this.cc || '')
        .split(/,|\s/)
        .map(email => email.trim())
        .filter(email => !!email)
        .map(email => ({ email }));
      if (documentClone.cc.length === 0) {
        this.deleteDocumentAttribute(documentClone, 'cc', null);
      }

      documentClone.reply_to = this.replyTo;
      if (documentClone?.reply_to?.length === 0) this.deleteDocumentAttribute(documentClone, 'reply_to', null);

      if (!documentClone.qualified) {
        documentClone.configs.lock_user_data = !this.isUpdatedUserData;
      }
    }
    if (documentClone.locale.country !== 'BR') {
      documentClone.ignore_cpf = true;
    }
    if (!documentClone.email_template_id || documentClone.email_template_id === -1) {
      if (this.isNewDocument) {
        // se for um documento novo, nao é possivel atribuir 'null' então precisa remover o atributo
        this.deleteDocumentAttribute(documentClone, 'email_template_id');
      } else {
        // permite remover um email_template_id e voltar a usar o template padrão
        documentClone.email_template_id = null;
      }
    }
    if (!documentClone.whatsapp_template) this.deleteDocumentAttribute(documentClone, 'whatsapp_template');

    return documentClone;
  }

  switchChangeFooter() {
    this.document.footer = this.document.footer ? FooterEnum.Bottom : null;
  }

  switchChangeReminder() {
    if (this.showReminder && !this.document.refusable) {
      this.currentUser.additional_configs.miscellaneous_section = false;
    }
    this.document.refusable = this.showReminder || this.document.refusable;
    this.document.reminder = this.document.reminder ? this.document.reminder : ReminderEnum.Weekly;
  }

  switchChangeExpiration() {
    this.document.expiration = this.document.expiration ? this.document.expiration : ({} as DocumentInput['expiration']);
    this.document.expiration.days_before = this.document.expiration.days_before ? this.document.expiration.days_before : 7;
    const notifyAt = this.document.expiration.notify_at ? this.document.expiration.notify_at : new Date(new Date().getTime() + 31536000000).toLocaleDateString('pt-BR');
    this.document.expiration.notify_at = this.dateService.normalizeDate(notifyAt);
  }

  switchSignatureAppearance() {
    this.document.configs.signature_appearance = this.document.configs.signature_appearance ? SignatureAppearanceEnum.Handwriting : null;
  }

  switchChangeDeadline() {
    const thirtyDaysFromNow = new Date(new Date().getTime() + 2592000000);
    this.deadline.date = this.dateService.normalizeDate(this.deadline.date || thirtyDaysFromNow.toLocaleDateString('pt-BR'));
    this.deadline.time = this.deadline.time || '23:59';
    this.document.deadline_at = this.document.deadline_at || thirtyDaysFromNow.toJSON();
  }

  selectNewSignatureStyle(value: boolean) {
    if (this.isQualified) {
      if (!value) {
        this.showAuditPage = value;
        this.document.show_audit_page = value;
        this.document.footer = null;
      }
      this.showNewSignatureStyle = value;
      this.document.new_signature_style = value;
    }
    // this.documentChange.emit(this.document);
  }

  switchAuditPage() {
    if (this.document.qualified) {
      if (this.showNewSignatureStyle) {
        this.document.show_audit_page = !this.document.show_audit_page;
      }
    } else {
      if (!this.document.qualified) {
        this.document.show_audit_page = !this.document.show_audit_page;
      }
    }
  }

  openFoldersModal() {
    this.foldersListingService.openFoldersModal([], null, true, this.folder?.id).subscribe({
      next: (folder: Folder) => {
        this.folder = folder;
      }
    });
  }

  /**
   * Determines if the reminder can be edited by checking if the document's creation date (`documentCreatedAt`)
   *  1 day = 24 * 3.600 * 1.000 = 86400000 milliseconds
   */
  canEditReminder() {
    const expirationDate = new Date(new Date(this.documentCreatedAt).getTime() + 86400000 * 20);
    return expirationDate > new Date();
  }
  showReminderDailyOption() {
    const expirationDate = new Date(new Date(this.documentCreatedAt).getTime() + 86400000 * 6);
    return expirationDate > new Date() || this.isNewDocument || this.document.reminder === ReminderEnum.Daily;
  }

  parseAdditionalConfigs(document: DocumentInput) {
    const additionalConfigs = cloneDeep(this.currentUser.additional_configs);
    delete (additionalConfigs as any).__typename;

    additionalConfigs.days_before = document.expiration?.days_before || null;
    additionalConfigs.footer = document.footer || null;
    additionalConfigs.footer_type = document.footer_type || null;
    additionalConfigs.lock_user_data = document.configs?.lock_user_data || false;
    additionalConfigs.new_signature_style = document.new_signature_style || false;
    additionalConfigs.notify_watchers = document.configs?.notify_watchers || false;
    additionalConfigs.refusable = document.refusable || false;
    additionalConfigs.reminder = document.reminder || null;
    additionalConfigs.scrolling_required = document.scrolling_required || false;
    additionalConfigs.show_audit_page = document.show_audit_page || false;
    additionalConfigs.signature_appearance = document.configs?.signature_appearance || null;
    additionalConfigs.stop_on_rejected = document.stop_on_rejected || false;

    return additionalConfigs;
  }

  parseMemberConfigs(document: DocumentInput) {
    const memberConfigs = cloneDeep(this.currentUser.member.configs);
    delete (memberConfigs as any).__typename;
    delete memberConfigs.folder;

    memberConfigs.email_template = document.email_template_id || null;
    memberConfigs.ignore_birthdate = document.ignore_birthdate || false;
    memberConfigs.ignore_cpf = document.ignore_cpf || false;
    memberConfigs.locale = document.locale;
    memberConfigs.locked_cc = this.isLockedCC ? (document.cc || []).filter(cc => !!cc?.email).map(cc => cc.email.trim()) : [];
    memberConfigs.locked_reply_to = this.isLockedReplyTo ? document.reply_to : '';
    memberConfigs.locked_watchers = this.isLockedWatchers ? (document.watchers || []).filter(w => !!w).map(w => w.trim()) : [];
    memberConfigs.notification_signed = document.configs?.notification_signed || false;
    memberConfigs.notification_finished = document.configs?.notification_finished || false;
    memberConfigs.whatsapp_template = document.whatsapp_template || null;

    return memberConfigs;
  }

  private toggleSwitches(user?: User) {
    const currentUser = user || this.currentUser;
    if (this.isNewDocument && currentUser && currentUser.subscription.has_premium_features) {
      this.isLockedCC = currentUser.member.configs.locked_cc?.length > 0;
      this.isLockedReplyTo = currentUser.member.configs.locked_reply_to?.length > 0;
      this.isLockedWatchers = currentUser.member.configs.locked_watchers?.length > 0;
      this.showReminder = !!currentUser.additional_configs.reminder || false;
      this.showExpiration = !!currentUser.additional_configs.days_before || false;
    } else if (currentUser && !this.isNewDocument) {
      this.showReminder = !!(this.document || {}).reminder;
      this.showExpiration = !!((this.document || {}).expiration || {}).notify_at;
    }
  }

  private deleteDocumentAttribute(document: any, attribute: string, value?: any) {
    if (this.isNewDocument || value === undefined) {
      delete document[attribute];
    } else {
      document[attribute] = value;
    }
  }
}
